//Auth container

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import LoginForm from './LoginForm';
import SignupForm from './SignupForm';
import { ASSET_PATHS } from '../../utils/gameConfig';
import InstructorSignup from './InstructorSignup';

type AuthMode = 'choice' | 'login' | 'signup' | 'instructor';

const AuthContainer: React.FC = () => {
  const [authMode, setAuthMode] = useState<AuthMode>('choice');

  const backgroundStyle = {
    backgroundImage: `url(${ASSET_PATHS.BACKGROUNDS.LOGIN})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  };

  const containerVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    },
    exit: { 
      opacity: 0, 
      scale: 0.95,
      transition: { duration: 0.3 }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={backgroundStyle}>
      <div className="w-full max-w-md">
        <AnimatePresence mode="wait">
          {authMode === 'choice' && (
            <motion.div
              key="choice"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl p-8 border border-powder-200"
            >
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-powder-800 mb-2">
                  Community Forest
                </h1>
                <p className="text-powder-600">
                  Grow together, learn together
                </p>
              </div>

              <div className="space-y-4">
                <motion.button
                  onClick={() => setAuthMode('login')}
                  className="w-full py-4 bg-sage-500 hover:bg-sage-600 text-white rounded-xl font-medium text-lg transition-colors shadow-lg"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Log In
                </motion.button>

                <motion.button
                  onClick={() => setAuthMode('signup')}
                  className="w-full py-4 bg-lavender-500 hover:bg-lavender-600 text-white rounded-xl font-medium text-lg transition-colors shadow-lg"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Sign Up
                </motion.button>

                {/* INSTRUCTOR SIGNUP */}
                <div className="mt-6 text-center">
                  <button
                    onClick={() => setAuthMode('instructor')}
                    className="text-sm text-powder-600 hover:text-powder-800 underline transition-colors"
                  >
                    Register as an Instructor
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {authMode === 'login' && (
            <motion.div
              key="login"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <LoginForm onBack={() => setAuthMode('choice')} />
            </motion.div>
          )}

          {authMode === 'signup' && (
            <motion.div
              key="signup"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <SignupForm onBack={() => setAuthMode('choice')} />
            </motion.div>
          )}

          {/* Instructor signup */}
          {authMode === 'instructor' && (
            <motion.div
              key="instructor"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <InstructorSignup onBack={() => setAuthMode('choice')} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AuthContainer;